// Tenant theme (Configurações › Marca › Tema do cliente, prototype "Usuários e permissões converter").
// The shape follows the prototype (Vuetify-style color names and component defaults) and the backend
// (TenantThemeRules); themeCss() maps it onto the Aubaine design-system tokens and @acme/ui classes.

export const THEME_COLOR_KEYS = [
  'primary',
  'primary-darken-1',
  'secondary',
  'secondary-darken-1',
  'success',
  'info',
  'warning',
  'error',
  'background',
  'surface',
  'surface-bright',
  'surface-light',
  'surface-variant',
  'on-surface-variant',
  'navigation',
] as const
export type ThemeColorKey = (typeof THEME_COLOR_KEYS)[number]
// Colors a theme may leave out (empty = automatic). Background of the active item of the side menu:
// without it, a tint of the menu's accent, as before (backend TenantThemeRules.OptionalColorKeys).
export const NAVIGATION_ACTIVE_COLOR = 'navigation-active'
export const OPTIONAL_THEME_COLOR_KEYS = [NAVIGATION_ACTIVE_COLOR] as const
export type ThemeColors = Record<string, string>

export const VARIATION_COLOR_KEYS = ['primary', 'secondary', 'success', 'info', 'warning', 'error'] as const
export const COMPONENT_COLOR_KEYS = ['primary', 'secondary', 'success', 'info', 'warning', 'error', 'surface', 'background', 'navigation'] as const
export const INITIAL_THEMES = ['light', 'dark', 'system'] as const
export const BUTTON_VARIANTS = ['elevated', 'flat', 'tonal', 'outlined', 'text', 'plain'] as const
export const BUTTON_ROUNDINGS = ['0', 'sm', 'default', 'lg', 'xl', 'pill'] as const
export const TEXT_FIELD_VARIANTS = ['outlined', 'filled', 'underlined', 'solo', 'plain'] as const
export const CHIP_VARIANTS = ['tonal', 'flat', 'elevated', 'outlined', 'text'] as const
export const MAX_VARIATION_STEPS = 5
export const MAX_CUSTOM_CSS_BYTES = 102_400

export interface ThemeSettings {
  initialTheme: string
  light: ThemeColors
  dark: ThemeColors
  variations: { colors: string[]; lighten: number; darken: number }
  components: {
    button: { color: string; variant: string; rounded: string }
    textField: { color: string; variant: string }
    chip: { color: string; variant: string }
    appBar: { color: string }
    navigationDrawer: { color: string }
  }
}

export interface TenantThemeDraft {
  settings: ThemeSettings
  customCss: string
  customCssFileName: string | null
}

export function defaultThemeSettings(): ThemeSettings {
  return {
    initialTheme: 'light',
    light: {
      primary: '#1867C0',
      'primary-darken-1': '#1F5592',
      secondary: '#48A9A6',
      'secondary-darken-1': '#018786',
      success: '#4CAF50',
      info: '#2196F3',
      warning: '#FB8C00',
      error: '#B00020',
      background: '#FFFFFF',
      surface: '#FFFFFF',
      'surface-bright': '#FFFFFF',
      'surface-light': '#EEEEEE',
      'surface-variant': '#424242',
      'on-surface-variant': '#EEEEEE',
      navigation: '#FFFFFF',
    },
    dark: {
      primary: '#2196F3',
      'primary-darken-1': '#277CC1',
      secondary: '#54B6B2',
      'secondary-darken-1': '#48A9A6',
      success: '#4CAF50',
      info: '#2196F3',
      warning: '#FB8C00',
      error: '#CF6679',
      background: '#121212',
      surface: '#212121',
      'surface-bright': '#CCBFD6',
      'surface-light': '#424242',
      'surface-variant': '#A3A3A3',
      'on-surface-variant': '#424242',
      navigation: '#212121',
    },
    variations: { colors: ['primary', 'secondary'], lighten: 1, darken: 2 },
    components: {
      button: { color: 'primary', variant: 'elevated', rounded: 'default' },
      textField: { color: 'primary', variant: 'outlined' },
      chip: { color: 'primary', variant: 'tonal' },
      appBar: { color: 'navigation' },
      navigationDrawer: { color: 'navigation' },
    },
  }
}

// ---- color math ----------------------------------------------------------------------------

export const isHex = (value: string | null | undefined): value is string => /^#[0-9a-f]{6}$/i.test(value ?? '')

const rgbOf = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16))
const hexOf = (rgb: number[]) =>
  '#' + rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('').toUpperCase()

function luminance(hex: string): number {
  const [r = 0, g = 0, b = 0] = rgbOf(hex).map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// WCAG contrast ratio between two colors (1 to 21).
export function contrast(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number]
  return (light + 0.05) / (dark + 0.05)
}

// Black or white, whichever reads better on the color (WCAG contrast).
export function onColor(hex: string): '#FFFFFF' | '#000000' {
  const l = luminance(hex)
  return 1.05 / (l + 0.05) >= (l + 0.05) / 0.05 ? '#FFFFFF' : '#000000'
}

export function mix(hex: string, target: string, amount: number): string {
  const a = rgbOf(hex)
  const b = rgbOf(target)
  return hexOf(a.map((v, i) => v + ((b[i] ?? 0) - v) * amount))
}

// Lighter (+1…) and darker (−1…) shades, 16% towards white/black per step (as in the prototype).
export function shades(hex: string, lighten: number, darken: number) {
  const out: { name: string; hex: string; step: number }[] = []
  for (let i = lighten; i >= 1; i--) out.push({ name: `lighten-${i}`, hex: mix(hex, '#FFFFFF', i * 0.16), step: i })
  out.push({ name: 'base', hex, step: 0 })
  for (let i = 1; i <= darken; i++) out.push({ name: `darken-${i}`, hex: mix(hex, '#000000', i * 0.16), step: -i })
  return out
}

// A color that is not a valid #RRGGBB falls back to the default of the same mode.
export function resolvedColors(settings: ThemeSettings, mode: 'light' | 'dark'): Record<ThemeColorKey, string> {
  const base = defaultThemeSettings()[mode]
  const source = settings[mode]
  return Object.fromEntries(
    THEME_COLOR_KEYS.map((key) => [key, isHex(source[key]) ? source[key]!.toUpperCase() : base[key]!]),
  ) as Record<ThemeColorKey, string>
}

export const hasInvalidColors = (settings: ThemeSettings) =>
  (['light', 'dark'] as const).some(
    (mode) =>
      THEME_COLOR_KEYS.some((key) => !isHex(settings[mode][key])) ||
      OPTIONAL_THEME_COLOR_KEYS.some((key) => !!settings[mode][key] && !isHex(settings[mode][key])),
  )

// ---- custom CSS ------------------------------------------------------------------------------

// Same rules as the backend (TenantThemeRules.CustomCssProblem), shown while typing.
export function customCssWarnings(css: string): string[] {
  const warnings: string[] = []
  if (new Blob([css]).size > MAX_CUSTOM_CSS_BYTES) warnings.push('O CSS personalizado pode ter até 100 KB.')
  if (/@import/i.test(css)) warnings.push('@import não é permitido. Cole o conteúdo do arquivo diretamente.')
  if (/url\(\s*['"]?\s*(?!data:)([a-z][a-z0-9+.-]*:|\\|\/\/)|['"]\s*([a-z][a-z0-9+.-]*:)?\/\//i.test(css))
    warnings.push('Endereços de outros sites em url() são bloqueados no portal. Use imagens enviadas em Marca.')
  if (css.includes('<')) warnings.push('O CSS não pode conter o caractere <.')
  if (/expression\s*\(|javascript\s*:|-moz-binding|behavior\s*:/i.test(css))
    warnings.push('expression(), javascript: e -moz-binding não são permitidos.')
  if ((css.match(/\{/g) ?? []).length !== (css.match(/\}/g) ?? []).length) warnings.push('Há chaves { } sem par. Revise o código.')
  return warnings
}

// ---- CSS generation --------------------------------------------------------------------------

const tint = (color: string, percent: number) => `color-mix(in srgb, ${color} ${percent}%, transparent)`
const cor = (key: string) => `var(--cor-${key})`

// Variables of one mode: the prototype's --cor-* (what the custom CSS redefines) and the design
// tokens that read them. Text colors follow the surface, so a dark surface in the light theme still reads.
function modeVariables(settings: ThemeSettings, mode: 'light' | 'dark'): string {
  const colors = resolvedColors(settings, mode)
  const lines: string[] = []
  for (const key of THEME_COLOR_KEYS) lines.push(`--cor-${key}: ${colors[key]};`)
  for (const key of settings.variations.colors) {
    const hex = colors[key as ThemeColorKey]
    if (!hex) continue
    for (const shade of shades(hex, settings.variations.lighten, settings.variations.darken)) {
      if (shade.step !== 0) lines.push(`--cor-${key}-${shade.name}: ${shade.hex};`)
    }
  }
  const text = onColor(colors.surface)
  for (const key of THEME_COLOR_KEYS) lines.push(`--cor-on-${key}: ${onColor(colors[key])};`)
  lines.push(`--cor-navigation-accent: ${contrast(colors.primary, colors.navigation) >= 3 ? cor('primary') : cor('on-navigation')};`)
  // Chosen active item color, with readable text; "initial" (guaranteed-invalid) leaves the automatic
  // one, and keeps the light theme's choice from leaking into the dark one.
  const active = settings[mode][NAVIGATION_ACTIVE_COLOR]
  lines.push(
    isHex(active)
      ? `--cor-navigation-active: ${active!.toUpperCase()}; --cor-on-navigation-active: ${onColor(active!)};`
      : '--cor-navigation-active: initial; --cor-on-navigation-active: initial;',
  )
  lines.push(
    `--brand-accent: ${cor('primary')};`,
    `--accent: ${cor('primary')};`,
    `--on-accent: ${cor('on-primary')};`,
    `--text-link: ${cor('primary')};`,
    `--system-green: ${cor('success')};`,
    `--status-success: ${cor('success')};`,
    `--status-info: ${cor('info')};`,
    `--system-orange: ${cor('warning')};`,
    `--status-warning: ${cor('warning')};`,
    `--system-red: ${cor('error')};`,
    `--status-danger: ${cor('error')};`,
    `--text-destructive: ${cor('error')};`,
    `--tint-success-bg: ${tint(cor('success'), 16)};`,
    `--tint-success-fg: color-mix(in srgb, ${cor('success')} 78%, ${text});`,
    `--tint-warning-bg: ${tint(cor('warning'), 16)};`,
    `--tint-warning-fg: color-mix(in srgb, ${cor('warning')} 78%, ${text});`,
    `--tint-danger-bg: ${tint(cor('error'), 14)};`,
    `--tint-danger-fg: color-mix(in srgb, ${cor('error')} 78%, ${text});`,
    `--tint-accent-bg: ${tint(cor('primary'), 10)};`,
    `--tint-accent-strong-bg: ${tint(cor('primary'), 16)};`,
    `--surface-grouped: ${cor('background')};`,
    `--surface-page: ${cor('background')};`,
    `--system-grouped-background: ${cor('background')};`,
    `--surface-card: ${cor('surface')};`,
    `--secondary-system-grouped-background: ${cor('surface')};`,
    `--system-background: ${cor('surface')};`,
    `--surface-elevated: ${cor('surface-bright')};`,
    `--surface-control: ${cor('surface-light')};`,
    `--label: ${text};`,
    `--text-primary: ${text};`,
    `--secondary-label: ${tint(text, 62)};`,
    `--text-secondary: ${tint(text, 62)};`,
    `--tertiary-label: ${tint(text, 32)};`,
    `--text-tertiary: ${tint(text, 32)};`,
    `--text-placeholder: ${tint(text, 32)};`,
    `--separator: ${tint(text, 14)};`,
    `--opaque-separator: ${tint(text, 22)};`,
    `color: ${text};`,
  )
  return lines.join('\n  ')
}

const RADIUS: Record<string, string | null> = { 0: '0', sm: '2px', default: null, lg: '8px', xl: '24px', pill: '999px' }

// Component defaults, as rules on the @acme/ui classes inside the scope.
function componentRules(settings: ThemeSettings, scope: string): string {
  const { button, textField, chip, appBar, navigationDrawer } = settings.components
  const rules: string[] = []
  const rule = (selector: string, body: string) => rules.push(`${selector} { ${body} }`)

  // Buttons: the color tints every button but the destructive ones; the variant is the primary action's look.
  const b = cor(button.color)
  const onB = cor(`on-${button.color}`)
  rule(`${scope} .ui-button:not(.ui-button--destructive)`, `--tint: ${b};`)
  const filled = `${scope} .ui-button--filled:not(.ui-button--destructive)`
  const buttonLooks: Record<string, string> = {
    elevated: `background: ${b}; color: ${onB}; box-shadow: 0 1px 2px rgba(0,0,0,.16);`,
    flat: `background: ${b}; color: ${onB}; box-shadow: none;`,
    tonal: `background: ${tint(b, 14)}; color: ${b}; box-shadow: none;`,
    outlined: `background: transparent; color: ${b}; box-shadow: inset 0 0 0 1px ${b};`,
    text: `background: transparent; color: ${b}; box-shadow: none;`,
    plain: `background: transparent; color: ${b}; box-shadow: none; opacity: .62;`,
  }
  // Entry screens (AccessPage): the primary action is .abtn--primary.
  rule(`${filled}, ${scope} .access .abtn--primary`, buttonLooks[button.variant] ?? buttonLooks.elevated!)
  const radius = RADIUS[button.rounded]
  if (radius) rule(`${scope} .ui-button, ${scope} .access .abtn--primary, ${scope} .access .abtn--secondary`, `border-radius: ${radius};`)
  // AccessPage sets its brand variables inline (from the brand color); the theme wins over them.
  rule(
    `${scope} .access`,
    `--brand: ${b} !important; --on-brand: ${onB} !important; --brand-link: ${cor('primary')} !important; --brand-tile: ${cor('primary')} !important; --on-tile: ${cor('on-primary')} !important;`,
  )

  // Text fields and selects.
  const fields = `${scope} .ui-field__input, ${scope} .ui-select__control, ${scope} .access .field__input`
  const focused = `${scope} .ui-field__input:focus, ${scope} .ui-select__control:focus, ${scope} .access .field__input:focus`
  const f = cor(textField.color)
  // Focus is the border in the field color, with no ring (management look, 07/10).
  rule(focused, `outline: none; border-color: ${f}; box-shadow: none;`)
  const fieldLooks: Record<string, string> = {
    outlined: '',
    filled: `border: 0; border-bottom: 2px solid var(--opaque-separator); border-radius: 6px 6px 0 0; background: ${tint('var(--label)', 6)};`,
    underlined: 'border: 0; border-bottom: 1px solid var(--opaque-separator); border-radius: 0; background: transparent;',
    solo: 'border: 0; background: var(--surface-card); box-shadow: 0 2px 4px rgba(0,0,0,.18);',
    plain: 'border: 0; border-radius: 0; background: transparent;',
  }
  if (fieldLooks[textField.variant]) rule(fields, fieldLooks[textField.variant]!)
  if (textField.variant === 'filled' || textField.variant === 'underlined') {
    rule(
      focused,
      `box-shadow: none; border-bottom-color: ${f}; border-bottom-width: 2px;`,
    )
  }

  // Chips (UiPill): accent pills take the chip color; status pills keep their state color. The
  // variant applies to all of them.
  const tones: [string, string][] = [
    ['accent', chip.color],
    ['success', 'success'],
    ['warning', 'warning'],
    ['danger', 'error'],
  ]
  for (const [tone, key] of tones) {
    const c = cor(key)
    const looks: Record<string, string> = {
      tonal: tone === 'accent' ? `background: ${tint(c, 14)}; color: ${c};` : '',
      flat: `background: ${c}; color: ${cor(`on-${key}`)};`,
      elevated: `background: ${c}; color: ${cor(`on-${key}`)}; box-shadow: 0 1px 3px rgba(0,0,0,.24);`,
      outlined: `background: transparent; color: ${c}; box-shadow: inset 0 0 0 1px ${c};`,
      text: `background: transparent; color: ${c}; padding-inline: 2px;`,
    }
    const body = looks[chip.variant] ?? ''
    if (body) rule(`${scope} .ui-pill--${tone}`, body)
  }

  // Top bar and side menu of the portal. Text inside them follows their color; the popups that
  // open from the bar (user menu, notifications) keep the surface colors.
  const onVars = (key: string) => {
    const on = cor(`on-${key}`)
    return `--label: ${on}; --text-primary: ${on}; --secondary-label: ${tint(on, 70)}; --text-secondary: ${tint(on, 70)}; --tertiary-label: ${tint(on, 50)}; --separator: ${tint(on, 18)}; color: ${on};`
  }
  // The accent (active item, focus) turns into the bar's text color, or it would vanish on it. The menu
  // color keeps the brand's primary when it reads on it (white menu, colored active item), and the
  // dividing line, since it usually sits next to a different background.
  const accentOn = (key: string) => (key === 'navigation' ? cor('navigation-accent') : cor(`on-${key}`))
  const border = (key: string, side: 'bottom' | 'right') => (key === 'navigation' ? '' : `border-${side}-color: transparent;`)
  if (appBar.color !== 'surface') {
    const accent = accentOn(appBar.color)
    rule(`${scope} .shell__header`, `background: ${cor(appBar.color)}; ${border(appBar.color, 'bottom')} ${onVars(appBar.color)} --accent: ${accent}; --tint-accent-bg: ${tint(accent, 18)};`)
    rule(
      `${scope} .shell__menu, ${scope} .bell__panel`,
      `--label: var(--text-on-surface); --text-primary: var(--text-on-surface); --secondary-label: ${tint('var(--text-on-surface)', 62)}; --text-secondary: ${tint('var(--text-on-surface)', 62)}; --accent: ${cor('primary')}; color: var(--text-on-surface);`,
    )
  }
  if (navigationDrawer.color !== 'surface') {
    const accent = accentOn(navigationDrawer.color)
    rule(
      `${scope} .shell__sidebar`,
      `background: ${cor(navigationDrawer.color)}; ${border(navigationDrawer.color, 'right')} ${onVars(navigationDrawer.color)} --accent: ${accent}; --tint-accent-bg: ${tint(accent, 18)}; --tint-accent-strong-bg: ${tint(accent, 24)};`,
    )
  } else {
    // The default menu is dark (AppShell redefines the text tokens inside it); on the surface color they go back
    // to the surface's text.
    rule(`${scope} .shell__sidebar`, `background: ${cor('surface')}; ${onVars('surface')} --tertiary-label: ${tint(cor('on-surface'), 45)}; --accent: ${cor('primary')}; --on-accent: ${cor('on-primary')}; --tint-accent-bg: ${tint(cor('primary'), 10)}; --tint-accent-strong-bg: ${tint(cor('primary'), 16)};`)
  }
  rule(
    `${scope} .shell__sidebar .shell__nav-item--active`,
    'background: var(--cor-navigation-active, var(--tint-accent-bg)); color: var(--cor-on-navigation-active, var(--accent));',
  )
  return rules.join('\n')
}

export interface ThemeCssOptions {
  // Element the theme applies to (':root[data-tenant-theme]' in the portal, '[data-theme-preview]'
  // in the editor).
  scope: string
  // Forces one mode (the editor preview); without it, light and dark follow the page's data-theme
  // and the system preference, like the design tokens do.
  mode?: 'light' | 'dark'
}

export function themeCss(settings: ThemeSettings, { scope, mode }: ThemeCssOptions): string {
  const onSurface = (m: 'light' | 'dark') => `--text-on-surface: ${onColor(resolvedColors(settings, m).surface)};`
  const vars = (m: 'light' | 'dark') => `${modeVariables(settings, m)}\n  ${onSurface(m)}`
  const blocks: string[] = []
  if (mode) {
    blocks.push(`${scope} {\n  ${vars(mode)}\n}`)
  } else {
    const root = scope.startsWith(':root') ? scope : `:root ${scope}`
    const dark = scope.startsWith(':root') ? `${scope}[data-theme="dark"]` : `:root[data-theme="dark"] ${scope}`
    const systemDark = scope.startsWith(':root') ? `${scope}:not([data-theme="light"])` : `:root:not([data-theme="light"]) ${scope}`
    blocks.push(`${root} {\n  ${vars('light')}\n}`)
    blocks.push(`@media (prefers-color-scheme: dark) {\n${systemDark} {\n  ${vars('dark')}\n}\n}`)
    blocks.push(`${dark} {\n  ${vars('dark')}\n}`)
  }
  blocks.push(componentRules(settings, scope))
  return blocks.join('\n')
}

// The custom CSS, applied after the theme and limited to the scope. `:scope` inside it is the
// scope element (as in the prototype). Returns null for an empty CSS.
export function scopedCustomCss(css: string, scope: string): string | null {
  return css.trim() ? `@scope (${scope}) {\n${css}\n}` : null
}

// ---- import / export (the prototype's file format, "configuracao-de-cores") -------------------

type Loose = Record<string, unknown>
const isObject = (value: unknown): value is Loose => typeof value === 'object' && value !== null && !Array.isArray(value)
const str = (value: unknown) => (typeof value === 'string' || typeof value === 'number' ? String(value) : undefined)

// Accepts the file exported here, the prototype's (Vuetify names: themes/defaults/VBtn…), or just a
// { light, dark } palette. Returns null when nothing usable is found.
export function importTheme(data: unknown): TenantThemeDraft | null {
  if (!isObject(data)) return null
  const source = isObject(data.tema) ? data.tema : data
  const themes = isObject(source.themes) ? source.themes : source
  if (!isObject(themes.light) && !isObject(themes.dark)) return null

  const settings = defaultThemeSettings()
  for (const mode of ['light', 'dark'] as const) {
    const raw = themes[mode]
    const colors = isObject(raw) && isObject(raw.colors) ? raw.colors : raw
    if (!isObject(colors)) continue
    for (const key of [...THEME_COLOR_KEYS, ...OPTIONAL_THEME_COLOR_KEYS]) {
      const value = str(colors[key])
      if (isHex(value)) settings[mode][key] = value.toUpperCase()
    }
    // Files from before the menu color: the menu keeps the surface it had.
    if (!isHex(str(colors.navigation))) settings[mode].navigation = settings[mode].surface!
  }

  const initial = str(source.initialTheme ?? source.defaultTheme)
  if (initial && (INITIAL_THEMES as readonly string[]).includes(initial)) settings.initialTheme = initial

  if (isObject(source.variations)) {
    const v = source.variations
    if (Array.isArray(v.colors))
      settings.variations.colors = VARIATION_COLOR_KEYS.filter((key) => (v.colors as unknown[]).includes(key))
    for (const part of ['lighten', 'darken'] as const) {
      const n = Number(v[part])
      if (Number.isInteger(n) && n >= 0 && n <= MAX_VARIATION_STEPS) settings.variations[part] = n
    }
  }

  const defaults = isObject(source.components) ? source.components : isObject(source.defaults) ? source.defaults : null
  if (defaults) {
    const pick = (names: string[]) => names.map((name) => defaults[name]).find(isObject)
    const set = <T extends Record<string, string>>(target: T, from: Loose | undefined, allowed: Record<keyof T, readonly string[]>) => {
      if (!from) return
      for (const prop of Object.keys(allowed) as (keyof T)[]) {
        const value = str(from[prop as string])
        if (value && allowed[prop].includes(value)) target[prop] = value as T[keyof T]
      }
    }
    const c = settings.components
    set(c.button, pick(['button', 'VBtn']), { color: COMPONENT_COLOR_KEYS, variant: BUTTON_VARIANTS, rounded: BUTTON_ROUNDINGS })
    set(c.textField, pick(['textField', 'VTextField']), { color: COMPONENT_COLOR_KEYS, variant: TEXT_FIELD_VARIANTS })
    set(c.chip, pick(['chip', 'VChip']), { color: COMPONENT_COLOR_KEYS, variant: CHIP_VARIANTS })
    set(c.appBar, pick(['appBar', 'VAppBar']), { color: COMPONENT_COLOR_KEYS })
    set(c.navigationDrawer, pick(['navigationDrawer', 'VNavigationDrawer']), { color: COMPONENT_COLOR_KEYS })
  }

  const css = typeof source.customCss === 'string' ? source.customCss.slice(0, MAX_CUSTOM_CSS_BYTES) : ''
  const file = typeof source.customCssFileName === 'string' ? source.customCssFileName : typeof source.customCssFile === 'string' ? source.customCssFile : null
  return { settings, customCss: css, customCssFileName: css ? file || null : null }
}

// File compatible with the prototype's "Exportar": Vuetify-style names inside "tema".
export function exportTheme(draft: TenantThemeDraft, tenantName: string, now = new Date()) {
  const { settings } = draft
  const c = settings.components
  return {
    tipo: 'configuracao-de-cores',
    versao: 1,
    cliente: tenantName,
    exportadoEm: now.toISOString(),
    tema: {
      defaultTheme: settings.initialTheme,
      themes: { light: { ...settings.light }, dark: { ...settings.dark } },
      variations: { colors: [...settings.variations.colors], lighten: String(settings.variations.lighten), darken: String(settings.variations.darken) },
      defaults: {
        VBtn: { ...c.button },
        VTextField: { ...c.textField },
        VChip: { ...c.chip },
        VAppBar: { ...c.appBar },
        VNavigationDrawer: { ...c.navigationDrawer },
      },
      customCss: draft.customCss,
      customCssFile: draft.customCssFileName ?? '',
    },
  }
}

export function slugOf(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'cliente'
  )
}
